import { DEFAULT_CONSENT_OPTIONS, type ConsentParams } from '../../constants';

/**
 * Serialize and normalize Google Consent Mode options into a safe, predictable shape.
 *
 * Why this exists:
 * - Host pages can send partial or legacy payloads.
 * - Values can be invalid or missing. We only accept 'granted' | 'denied'.
 * - Downstream code expects a complete, validated object for all consent keys.
 *
 * What it does:
 * - If no options are provided, returns all keys set to 'denied'.
 * - Coerces any non-'granted'/'denied' value to 'denied'.
 *
 * Example
 *   normalizeConsentParams({ analytics_storage: 'granted' }) => {
 *     ad_storage: 'denied',
 *     ad_user_data: 'denied',
 *     ad_personalization: 'denied',
 *     analytics_storage: 'granted',
 *   }
 */
const normalizeConsentParams = (options?: ConsentParams): ConsentParams => {
  if (!options) return DEFAULT_CONSENT_OPTIONS;

  const normalized = (
    Object.keys(DEFAULT_CONSENT_OPTIONS) as Array<keyof ConsentParams>
  ).reduce((acc, key) => {
    acc[key] =
      options[key] === 'granted' || options[key] === 'denied'
        ? options[key]
        : 'denied';
    return acc;
  }, {} as ConsentParams);

  return normalized;
};

export default normalizeConsentParams;
